import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import API from '@/config/api';
import {
  Crown, CheckCircle, AlertTriangle, Calendar, CreditCard, 
  ArrowUpCircle, ArrowDownCircle, RefreshCw, Shield, Star,
  TrendingUp, GraduationCap, Users, MessageSquare, Zap
} from 'lucide-react';

export default function SubscriptionPage() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchSubscription();
  }, [isAuthenticated, navigate]);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/subscription/details`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      } else {
        setError('Failed to load subscription details');
      }
    } catch (err) {
      setError('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? You will continue to have access until the end of your billing period.')) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(`${API}/subscription/cancel`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchSubscription();
        alert('Your subscription has been cancelled. You will continue to have access until your billing period ends.');
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to cancel subscription');
      }
    } catch (err) {
      alert('Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setActionLoading(true);
      const response = await fetch(`${API}/subscription/reactivate`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchSubscription();
        alert('Your subscription has been reactivated!');
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to reactivate subscription');
      }
    } catch (err) {
      alert('Failed to reactivate subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpgrade = async (packageId) => {
    try {
      setActionLoading(true);
      const response = await fetch(`${API}/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          package_id: packageId,
          package_type: 'membership',
          origin_url: window.location.origin
        })
      });

      const data = await response.json();

      if (data.success && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert(data.detail || 'Failed to start checkout');
      }
    } catch (err) {
      alert('Failed to start upgrade process');
    } finally {
      setActionLoading(false);
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'dynasty_builder': return <Crown className="h-6 w-6 text-[#D4AF37]" />;
      case 'wealth_architect': return <Star className="h-6 w-6 text-blue-400" />;
      default: return <Shield className="h-6 w-6 text-gray-400" />;
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'dynasty_builder': return 'border-[#D4AF37] bg-[#D4AF37]/10';
      case 'wealth_architect': return 'border-blue-500 bg-blue-500/10';
      default: return 'border-gray-600 bg-gray-600/10';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Card className="bg-[#14120f] border-red-500/30 max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-400">{error}</p>
            <Button onClick={fetchSubscription} className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" /> Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] py-12 px-4" data-testid="subscription-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Subscription Management</h1>
          <p className="text-gray-400">Manage your NRL University membership</p>
        </div>

        {/* Current Plan Card */}
        <Card className={`mb-8 ${getTierColor(subscription?.membership_tier)}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getTierIcon(subscription?.membership_tier)}
                <div>
                  <CardTitle className="text-white text-xl">{subscription?.tier_name}</CardTitle>
                  <CardDescription className="text-gray-400">Current Plan</CardDescription>
                </div>
              </div>
              <div className="text-right">
                {subscription?.tier_price > 0 ? (
                  <>
                    <p className="text-2xl font-bold text-[#D4AF37]">${subscription?.tier_price}</p>
                    <p className="text-sm text-gray-400">/{subscription?.tier_period}</p>
                  </>
                ) : (
                  <Badge className="bg-emerald-500/20 text-emerald-400">FREE</Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Status */}
            <div className="flex items-center gap-4 mb-6 p-4 bg-[#0a0a0f] rounded-lg">
              {subscription?.is_active ? (
                <>
                  <CheckCircle className="h-5 w-5 text-emerald-400" />
                  <div>
                    <p className="text-emerald-400 font-medium">Active Subscription</p>
                    {subscription?.expires && (
                      <p className="text-sm text-gray-400">
                        {subscription?.days_remaining !== null 
                          ? `Renews in ${subscription.days_remaining} days` 
                          : `Valid until ${new Date(subscription.expires).toLocaleDateString()}`}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-5 w-5 text-yellow-400" />
                  <div>
                    <p className="text-yellow-400 font-medium">Subscription Expired</p>
                    <p className="text-sm text-gray-400">Upgrade to regain access to premium features</p>
                  </div>
                </>
              )}
            </div>

            {/* Features */}
            <div className="mb-6">
              <h3 className="text-white font-medium mb-3">Your Features</h3>
              <div className="grid md:grid-cols-2 gap-2">
                {subscription?.tier_features?.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-[#D4AF37]" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              {subscription?.can_upgrade && (
                <Button 
                  onClick={() => handleUpgrade(subscription?.membership_tier === 'legacy_explorer' ? 'wealth_architect_monthly' : 'dynasty_builder_monthly')}
                  className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                  disabled={actionLoading}
                >
                  <ArrowUpCircle className="h-4 w-4 mr-2" /> Upgrade Plan
                </Button>
              )}
              
              {subscription?.tier_price > 0 && subscription?.is_active && (
                <Button 
                  onClick={handleCancelSubscription}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  disabled={actionLoading}
                >
                  Cancel Subscription
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Available Plans */}
        {subscription?.can_upgrade && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4">Available Upgrades</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {subscription?.membership_tier === 'legacy_explorer' && (
                <>
                  <Card className="bg-[#14120f] border-blue-500/30 hover:border-blue-500/60 transition-colors">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Star className="h-5 w-5 text-blue-400" />
                        <CardTitle className="text-white">Wealth Architect</CardTitle>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-blue-400">$497</span>
                        <span className="text-gray-400">/month</span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-center gap-2 text-sm text-gray-300">
                          <GraduationCap className="h-4 w-4 text-blue-400" /> NRL University Courses
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-300">
                          <Users className="h-4 w-4 text-blue-400" /> Community Access
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-300">
                          <MessageSquare className="h-4 w-4 text-blue-400" /> Email Support
                        </li>
                      </ul>
                      <Button 
                        onClick={() => handleUpgrade('wealth_architect_monthly')}
                        className="w-full bg-blue-500 hover:bg-blue-600"
                        disabled={actionLoading}
                      >
                        Upgrade to Wealth Architect
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="bg-[#14120f] border-[#D4AF37]/30 hover:border-[#D4AF37]/60 transition-colors">
                    <CardHeader>
                      <Badge className="w-fit bg-[#D4AF37] text-black mb-2">
                        <Zap className="h-3 w-3 mr-1" /> Best Value
                      </Badge>
                      <div className="flex items-center gap-2">
                        <Crown className="h-5 w-5 text-[#D4AF37]" />
                        <CardTitle className="text-white">Dynasty Builder</CardTitle>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-[#D4AF37]">$1,497</span>
                        <span className="text-gray-400">/month</span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-center gap-2 text-sm text-gray-300">
                          <TrendingUp className="h-4 w-4 text-[#D4AF37]" /> LIVE Trading Access
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-300">
                          <Users className="h-4 w-4 text-[#D4AF37]" /> 1-on-1 Coaching
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-300">
                          <GraduationCap className="h-4 w-4 text-[#D4AF37]" /> Full Course Library
                        </li>
                      </ul>
                      <Button 
                        onClick={() => handleUpgrade('dynasty_builder_monthly')}
                        className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                        disabled={actionLoading}
                      >
                        Upgrade to Dynasty Builder
                      </Button>
                    </CardContent>
                  </Card>
                </>
              )}

              {subscription?.membership_tier === 'wealth_architect' && (
                <Card className="bg-[#14120f] border-[#D4AF37]/30 hover:border-[#D4AF37]/60 transition-colors">
                  <CardHeader>
                    <Badge className="w-fit bg-[#D4AF37] text-black mb-2">
                      <Zap className="h-3 w-3 mr-1" /> Ultimate Tier
                    </Badge>
                    <div className="flex items-center gap-2">
                      <Crown className="h-5 w-5 text-[#D4AF37]" />
                      <CardTitle className="text-white">Dynasty Builder</CardTitle>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-[#D4AF37]">$1,497</span>
                      <span className="text-gray-400">/month</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-center gap-2 text-sm text-gray-300">
                        <TrendingUp className="h-4 w-4 text-[#D4AF37]" /> LIVE Trading Access
                      </li>
                      <li className="flex items-center gap-2 text-sm text-gray-300">
                        <Users className="h-4 w-4 text-[#D4AF37]" /> 1-on-1 Biweekly Coaching
                      </li>
                      <li className="flex items-center gap-2 text-sm text-gray-300">
                        <GraduationCap className="h-4 w-4 text-[#D4AF37]" /> Full Go-at-Your-Pace Courses
                      </li>
                      <li className="flex items-center gap-2 text-sm text-gray-300">
                        <Shield className="h-4 w-4 text-[#D4AF37]" /> Priority Support
                      </li>
                    </ul>
                    <Button 
                      onClick={() => handleUpgrade('dynasty_builder_monthly')}
                      className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                      disabled={actionLoading}
                    >
                      Upgrade to Dynasty Builder
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Payment History */}
        <Card className="bg-[#14120f] border-[#1c1814]">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[#D4AF37]" />
              <CardTitle className="text-white">Payment History</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {subscription?.payment_history?.length > 0 ? (
              <div className="space-y-3">
                {subscription.payment_history.map((payment, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#0a0a0f] rounded-lg">
                    <div>
                      <p className="text-white font-medium">{payment.description || payment.package_id}</p>
                      <p className="text-sm text-gray-400">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#D4AF37] font-bold">${payment.amount?.toFixed(2) || '0.00'}</p>
                      <Badge className={payment.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}>
                        {payment.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No payment history yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
